import React from "react";
import { GridColDef } from "@mui/x-data-grid-pro";
import {
  Avatar,
  Box,
  Chip,
  ClickAwayListener,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import PersonAddOutlined from "@mui/icons-material/PersonAddOutlined";
import CalendarToday from "@mui/icons-material/CalendarToday";
import ChatBubbleOutline from "@mui/icons-material/ChatBubbleOutline";
import { typeToIconMapping } from "../../../utils";
import HighestIcon from "@/assets/icons/highest.svg?react";
import HighIcon from "@/assets/icons/high.svg?react";
import MediumIcon from "@/assets/icons/medium.svg?react";
import LowIcon from "@/assets/icons/low.svg?react";
import moment from "moment";

export const getSeverityIcon = (severity: string) => {
  const style = {
    minWidth: "auto",
    minHeight: "auto",
    width: 20,
    height: 20,
  };
  switch (severity) {
    case "Highest":
      return <HighestIcon style={style} />;
    case "High":
      return <HighIcon style={style} />;
    case "Medium":
      return <MediumIcon style={style} />;
    case "Low":
      return <LowIcon style={style} />;
    default:
      return null;
  }
};

export const createColumns = (
  editingNameId: string | null,
  editedName: string,
  nameInputRef: React.RefObject<HTMLInputElement>,

  handleNameClick: (
    event: React.MouseEvent,
    taskId: string,
    currentName: string
  ) => void,
  handleNameChange: (event: React.ChangeEvent<HTMLInputElement>) => void,
  saveEditedName: () => void,
  handleNameKeyDown: (event: React.KeyboardEvent) => void,
  handleClickEdit: (
    event: React.MouseEvent,
    taskId: string,
    field: string
  ) => void,

  dateFormat: string,
  handleTaskClick: (taskId: string) => void
): GridColDef[] => {
  return [
    {
      field: "name",
      headerName: "Title",
      width: 300,
      flex: 1,
      renderCell: (params) => {
        const rowType = params?.row?.type || "";
        const typeInfo = rowType
          ? typeToIconMapping[rowType]
          : typeToIconMapping.TASK;
        const isEditing = editingNameId === params.row.id;
        const hasDateIssue = params.row.isOverdue || params.row.isOutOfSprint;

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              width: "100%",
              cursor: "pointer",
              position: "relative",
              overflow: "visible",
              height: "100%",
            }}
            onClick={() => handleTaskClick(params?.row?.id)}
          >
            {isEditing ? (
              <ClickAwayListener onClickAway={saveEditedName}>
                <TextField
                  size="small"
                  value={editedName}
                  onChange={handleNameChange}
                  inputRef={nameInputRef}
                  autoFocus
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "white",
                    },
                    "& .MuiInputBase-input": {
                      px: 1,
                      py: 0.5,
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                  }}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    handleNameKeyDown(e);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  inputProps={{
                    style: { fontSize: "0.875rem", fontWeight: 500 },
                  }}
                />
              </ClickAwayListener>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  width: "100%",
                }}
              >
                {rowType && (
                  <Box
                    component="span"
                    sx={{
                      width: 16,
                      height: 16,
                      mr: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: typeInfo?.bgcolor,
                      borderRadius: "3px",
                      color: "white",
                    }}
                  >
                    {typeInfo?.icon}
                  </Box>
                )}
                {params?.row?.bugKey && (
                  <span
                    style={{
                      marginRight: "4px",
                      color: hasDateIssue ? "#DC2626" : "#1976D2",
                      fontWeight: hasDateIssue ? 600 : 400,
                    }}
                  >
                    {params?.row?.bugKey}:
                  </span>
                )}
                <Typography
                  title={params?.value?.length > 30 ? params?.value : ""}
                  onClick={(e) => {
                    if (!isEditing) {
                      handleNameClick(e, params.row.id, params.value);
                    }
                  }}
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    color: hasDateIssue ? "#DC2626" : "inherit",
                    fontWeight: hasDateIssue ? 600 : 400,
                  }}
                >
                  {params?.value}
                </Typography>
              </Box>
            )}
          </Box>
        );
      },
    },
    {
      field: "assignTo",
      headerName: "Assignees",
      width: 150,
      renderCell: (params) => {
        const assignees = params.row.assignTo || [];
        const hasDateIssue = params.row.isOverdue || params.row.isOutOfSprint;

        if (params.row.isUnassigned) {
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                cursor: "pointer",
              }}
              onClick={(e) => handleClickEdit(e, params.row.id, "assignTo")}
            >
              <Chip
                label="Unassigned"
                size="small"
                color="warning"
                variant="outlined"
                sx={{
                  fontSize: 10,
                  height: 22,
                  color: hasDateIssue ? "#DC2626" : "inherit",
                  borderColor: hasDateIssue ? "#DC2626" : "inherit",
                }}
              />
            </Box>
          );
        }

        if (assignees.length === 0) {
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                height: "100%",
              }}
              onClick={(e) => handleClickEdit(e, params.row.id, "assignTo")}
            >
              <PersonAddOutlined
                sx={{
                  fontSize: 16,
                  color: hasDateIssue ? "#DC2626" : "text.secondary",
                }}
              />
            </Box>
          );
        }

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              cursor: "pointer",
            }}
            onClick={(e) => handleClickEdit(e, params.row.id, "assignTo")}
          >
            <Box sx={{ display: "flex", gap: 0.5 }}>
              {assignees
                .slice(0, 2)
                .map((assignee: { _id: string; fullName: string }) => (
                  <Tooltip key={assignee._id} title={assignee.fullName}>
                    <Avatar sx={{ width: 20, height: 20, fontSize: 9 }}>
                      {assignee.fullName
                        .split(" ")
                        .map((p) => p[0])
                        .join("")
                        .slice(0, 2)
                        .toUpperCase()}
                    </Avatar>
                  </Tooltip>
                ))}
              {assignees.length > 2 && (
                <Tooltip
                  title={assignees
                    .slice(2)
                    .map((a: any) => a.fullName)
                    .join(", ")}
                >
                  <Avatar
                    sx={{
                      width: 20,
                      height: 20,
                      fontSize: 9,
                      bgcolor: "#9CA3AF",
                    }}
                  >
                    +{assignees.length - 2}
                  </Avatar>
                </Tooltip>
              )}
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: hasDateIssue ? "#DC2626" : "#6B7280",
                fontWeight: hasDateIssue ? 600 : 400,
                fontSize: 11,
                ml: 0.5,
              }}
              noWrap
            >
              {assignees.length === 1
                ? assignees[0].fullName.split(" ")[0]
                : assignees.length === 2
                ? `${assignees[0].fullName.split(" ")[0]}, ${
                    assignees[1].fullName.split(" ")[0]
                  }`
                : `${assignees[0].fullName.split(" ")[0]} +${
                    assignees.length - 1
                  }`}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "date",
      headerName: "Due Date",
      width: 140,
      renderCell: (params) => {
        const hasDateIssue = params.row.isOverdue || params.row.isOutOfSprint;

        if (params.row.isUnscheduled) {
          return (
            <Box
              sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
              onClick={(e) => handleClickEdit(e, params.row.id, "dueDate")}
            >
              <Chip
                label="Unscheduled"
                size="small"
                color="info"
                variant="outlined"
                sx={{ fontSize: 10, height: 22 }}
              />
            </Box>
          );
        }

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              height: "100%",
              gap: 1,
            }}
            onClick={(e) => handleClickEdit(e, params.row.id, "dueDate")}
          >
            {params?.row?.dueDate ? (
              <>
                <Typography
                  variant="body2"
                  sx={{
                    color: hasDateIssue ? "#DC2626" : "inherit",
                    fontWeight: hasDateIssue ? 600 : 400,
                  }}
                >
                  {params?.row?.dueDate &&
                    moment(
                      params?.row?.dueDate,
                      "YYYY-MM-DDTHH:mm:ss:SSS"
                    )?.isValid() &&
                    moment(
                      params?.row?.dueDate,
                      "YYYY-MM-DDTHH:mm:ss:SSS"
                    )?.format(dateFormat || "DD MMM, YYYY")}
                </Typography>
                {hasDateIssue && (
                  <Chip
                    size="small"
                    label={params.row.isOverdue ? "Overdue" : "Out of Sprint"}
                    sx={{
                      height: 16,
                      fontSize: 8,
                      bgcolor: "#DC2626",
                      color: "white",
                      fontWeight: 600,
                    }}
                  />
                )}
              </>
            ) : (
              <CalendarToday
                sx={{
                  fontSize: "small",
                  color: hasDateIssue ? "#DC2626" : "text.secondary",
                }}
              />
            )}
          </Box>
        );
      },
    },
    {
      field: "type",
      headerName: "Type",
      width: 100,
      renderCell: (params) => {
        const hasDateIssue = params.row.isOverdue || params.row.isOutOfSprint;
        const getTypeColor = (type: string) => {
          if (hasDateIssue) return "error";
          switch (type) {
            case "BUG":
              return "error";
            case "STORY":
              return "success";
            case "DEFECT":
              return "warning";
            case "SUGGESTIONS":
              return "secondary";
            default:
              return "default";
          }
        };
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              height: "100%",
            }}
            onClick={(e) => handleClickEdit(e, params.row.id, "type")}
          >
            <Chip
              label={params.value}
              size="small"
              color={getTypeColor(params.value) as any}
              variant="outlined"
              sx={{
                fontWeight: hasDateIssue ? 600 : 400,
              }}
            />
          </Box>
        );
      },
    },
    {
      field: "severity",
      headerName: "Severity",
      width: 100,
      renderCell: (params) => {
        const hasDateIssue = params.row.isOverdue || params.row.isOutOfSprint;
        const getSeverityColor = (severity: string) => {
          if (hasDateIssue) return "error";
          switch (severity?.toLowerCase()) {
            case "high":
              return "error";
            case "medium":
              return "warning";
            case "low":
              return "success";
            default:
              return "default";
          }
        };
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              height: "100%",
              gap: 1,
            }}
            onClick={(e) => handleClickEdit(e, params.row.id, "severity")}
          >
            {getSeverityIcon(params?.value)}
            <Chip
              label={params.value}
              size="small"
              color={getSeverityColor(params.value) as any}
              variant="filled"
              sx={{
                minWidth: 60,
                fontWeight: hasDateIssue ? 600 : 400,
              }}
            />
          </Box>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => {
        const hasDateIssue = params.row.isOverdue || params.row.isOutOfSprint;
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              height: "100%",
            }}
            onClick={(e) => handleClickEdit(e, params.row.id, "bugStatus")}
          >
            <Typography
              variant="body2"
              sx={{
                color: hasDateIssue ? "#DC2626" : "inherit",
                fontWeight: hasDateIssue ? 600 : 400,
              }}
            >
              {params?.row?.status?.status ?? "-"}
            </Typography>
          </Box>
        );
      },
    },

    {
      field: "estimation",
      headerName: "Hours",
      width: 80,
      renderCell: (params) => {
        const hasDateIssue = params.row.isOverdue || params.row.isOutOfSprint;
        return (
          <Typography
            variant="body2"
            sx={{
              color: hasDateIssue ? "#DC2626" : "inherit",
              fontWeight: hasDateIssue ? 600 : 400,
            }}
          >
            {params.value}h
          </Typography>
        );
      },
    },
  ];
};
